import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/AppLayout";
import PreStartForm from "@/components/PreStartForm";
import PlateScanner from "@/components/PlateScanner";
import { toast } from "sonner";
import { Truck, AlertTriangle, ChevronRight, Search, CheckCircle2 } from "lucide-react";

export default function PreStart() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [machines, setMachines] = useState(null);
  const [checkedToday, setCheckedToday] = useState({});
  const [q, setQ] = useState("");
  const selectedId = params.get("machine");

  const load = () => {
    api.get("/machines").then(({ data }) => setMachines(data)).catch(() => setMachines([]));
    api.get("/prestart/today").then(({ data }) => setCheckedToday(data.checked || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const selected = useMemo(() => (machines || []).find((m) => m.id === selectedId), [machines, selectedId]);

  const select = (id) => { setParams(id ? { machine: id } : {}); };

  const onPlate = (data) => {
    if (!data.plate) return;
    if (data.matches.length >= 1) {
      toast.success(`Found ${data.matches[0].name} (${data.plate}) — starting pre-start`);
      select(data.matches[0].id);
    } else {
      toast.warning(`Read plate ${data.plate}, but no machine matches that rego.`);
      setQ(data.plate);
    }
  };

  const onDone = (data) => {
    if (data.offline) {
      toast.success("Saved offline — will sync automatically when you're back online");
      select(null);
      load();
      return;
    }
    if (data.overall === "fail") {
      toast.error(data.work_order_created ? "Fault flagged — urgent work order sent to your manager" : "Pre-start logged — fault flagged");
    } else if (data.flag_cleared) {
      toast.success("All clear — defect resolved, flag removed");
    } else {
      toast.success("Pre-start logged — all clear");
    }
    select(null);
    load();
  };

  const filtered = (machines || []).filter((m) => {
    const s = `${m.fleet_number || ""} ${m.make} ${m.model} ${m.type}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  if (selectedId && selected) {
    return (
      <div className="p-5 md:p-10 pb-24 max-w-3xl" data-testid="prestart-page">
        <button onClick={() => select(null)} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4" data-testid="prestart-back">
          <ChevronRight className="w-4 h-4 rotate-180" /> All machines
        </button>
        <PageHead title="Pre-Start" sub={`${selected.fleet_number ? selected.fleet_number + " · " : ""}${selected.make} ${selected.model}`} />
        <PreStartForm machineId={selected.id} defaultOperator={user?.name || ""} onDone={onDone} onCancel={() => select(null)} />
      </div>
    );
  }

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="prestart-page">
      <PageHead title="Pre-Start" sub="Pick a machine to run its pre-start check.">
        <PlateScanner onResult={onPlate} label="Scan Plate" testid="prestart-scan-plate" />
      </PageHead>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by fleet no., make or model…"
          className="il-input pl-10" data-testid="prestart-search" />
      </div>

      {machines === null ? (
        <p className="font-head text-2xl text-[#FFB800] uppercase tracking-widest">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="card p-10 text-center" data-testid="prestart-empty">
          <Truck className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="font-body text-[#94A3B8]">{machines.length === 0 ? "No machines in your fleet yet." : "No machines match your search."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="prestart-machine-grid">
          {filtered.map((m) => {
            const todayStatus = checkedToday[m.id];
            return (
            <button key={m.id} onClick={() => select(m.id)} data-testid={`prestart-select-${m.id}`}
              className="card p-4 text-left flex items-center justify-between gap-3 hover:border-[#FFB800]/50 transition-colors group">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 bg-[#23262F] flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-[#FFB800]" /></div>
                <div className="min-w-0">
                  <p className="font-body font-700 truncate">{m.fleet_number ? <span className="text-[#FFB800] font-mono mr-1">{m.fleet_number}</span> : null}{m.make} {m.model}</p>
                  <p className="font-mono text-[10px] text-[#94A3B8] uppercase tracking-widest">{m.type}</p>
                </div>
              </div>
              {m.flagged
                ? <span className="inline-flex items-center gap-1 bg-[#FF3B30] text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0" data-testid={`prestart-flag-${m.id}`}><AlertTriangle className="w-3 h-3" /> Flag</span>
                : todayStatus === "pass"
                  ? <span className="inline-flex items-center gap-1 bg-[#22C55E] text-black font-mono text-[10px] uppercase tracking-widest px-2 py-1 shrink-0" data-testid={`prestart-checked-${m.id}`}><CheckCircle2 className="w-3 h-3" /> Checked</span>
                  : <CheckCircle2 className="w-5 h-5 text-[#94A3B8] group-hover:text-[#FFB800] transition-colors shrink-0" />}
            </button>
          ); })}
        </div>
      )}
    </div>
  );
}
